<script setup lang="ts">
import type { OptionType } from 'element-plus/es/components/select-v2/src/select.types.mjs'
import { ref, computed } from 'vue'
import { Icon } from '@iconify/vue'
import { getAllCustomers } from '@/services/core.service'
import type { ICustomer } from '@/types/core/Customer'
import { getCustomerDisplayName } from '@/helpers/core/customers'
import { NotifyFail } from '@/helpers/core/notifications'
import { ClaimCostPayableTo } from '@/types/asicw/claims'

const emit = defineEmits<{
  change: [value: { entity: string; customer?: ICustomer }]
}>()

const props = withDefaults(
  defineProps<{
    disabled?: boolean
    placeholder?: string
  }>(),
  {
    disabled: false,
    placeholder: 'Entity',
  }
)

const selectedValue = defineModel<string>({ required: false })

// Default options for Servicer and Administrator
const defaultOptions = [
  {
    label: ClaimCostPayableTo.Administrator,
    value: ClaimCostPayableTo.Administrator,
    type: 'default' as const,
  },
  {
    label: ClaimCostPayableTo.Servicer,
    value: ClaimCostPayableTo.Servicer,
    type: 'default' as const,
  },
]

// Customer search state
const customerSearchLoading = ref<boolean>(false)
const customerSearchResults = ref<OptionType[]>([])

// If there's a selectedValue that's not a default option, add it to the search results
if (selectedValue.value && !defaultOptions.some((opt) => opt.value === selectedValue.value)) {
  // This is likely a customer name, we need to find the customer and add it to search results
  // For now, we'll add it as a generic option
  customerSearchResults.value.push({
    value: selectedValue.value,
    label: selectedValue.value,
    type: 'customer' as const,
  })
}

// Combined options (default + customer search results)
const allOptions = computed(() => {
  return [...defaultOptions, ...customerSearchResults.value]
})

/**
 * Customer `ElSelectV2` remote search method.
 *
 * @param query customer filter (search query).
 */
const searchCustomers = async (query: string) => {
  if (query == '') {
    customerSearchResults.value = []
    return
  }

  try {
    customerSearchLoading.value = true
    const results = await getAllCustomers({
      fragment: query,
      page: 1,
      per_page: 10,
    })
    customerSearchResults.value = results.data.map((customer: ICustomer) => {
      const displayName = getCustomerDisplayName(customer)
      return {
        label: displayName,
        value: displayName,
        customer: customer,
        type: 'customer' as const,
      }
    })
  } catch {
    NotifyFail('Customer search failed')
  } finally {
    customerSearchLoading.value = false
  }
}

const onChange = (val?: string) => {
  selectedValue.value = val

  if (!val) {
    emit('change', { entity: '' })
    return
  }

  // Check if it's a default option
  const defaultOption = defaultOptions.find((opt) => opt.value === val)
  if (defaultOption) {
    emit('change', { entity: val })
    return
  }

  // It's a customer selection
  const customerOption = customerSearchResults.value.find((opt) => opt.value === val)
  if (customerOption && 'customer' in customerOption) {
    emit('change', {
      entity: ClaimCostPayableTo.Customer,
      customer: customerOption.customer as ICustomer,
    })
  }
}
</script>

<template>
  <el-select-v2
    v-model="selectedValue"
    data-testid="entity-customer-select"
    :options="allOptions"
    value-key="value"
    remote
    :remote-method="searchCustomers"
    :loading="customerSearchLoading"
    no-data-text="No data"
    :placeholder="props.placeholder"
    clearable
    filterable
    :disabled="disabled"
    @change="onChange"
  >
    <template #loading>
      <div class="w-full inline-flex items-center justify-center">
        <Icon icon="eos-icons:three-dots-loading" height="20" />
      </div>
    </template>
    <template #header>
      <div>Type to search for customers or select default options</div>
    </template>
  </el-select-v2>
</template>
